import React, { useMemo, useRef } from 'react';
import {
  BarChart,
  Bar,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { Spinner } from 'react-bootstrap';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import ChartTitle from '../../../../../elements/ChartTitle';
import NoDataAvailable from '../NoDataAvailable';
import { createQueryString, getRandomColor } from '../../../../../../lib/utils';
import { fetchIssuesLoggedInJiraForCustomType } from '../../../../../../modules/dashboard';

const STATUS_COLORS = {
  'To Do': '#ffcc00',
  'In Progress': '#ff5733',
  Done: '#28a745',
  Blocked: '#d9534f',
  Review: '#007bff',
};

const IssuesLoggedPerStatusForCustomType = ({
  projectId,
  setShowExport,
  dateFilters,
  isJiraIntegrated,
  selectedJiraProject,
}) => {
  const jiraProjectId = selectedJiraProject?.linked_project_id;
  const forceRefreshRef = useRef(false);
  const queryClient = useQueryClient();
  const queryKey = [
    'issuesLoggedPerStatus',
    projectId,
    dateFilters,
    jiraProjectId,
  ];
  const { data: apiResponse, isLoading, isFetching } = useQuery({
    queryKey,
    enabled: isJiraIntegrated && !!selectedJiraProject,
    queryFn: async () => {
      const queryString = createQueryString(dateFilters);
      const forceRefresh = forceRefreshRef.current;
      forceRefreshRef.current = false;
      return await fetchIssuesLoggedInJiraForCustomType(
        projectId,
        queryString,
        jiraProjectId,
        forceRefresh
      );
    },
    onSuccess: () => {
      setShowExport(true);
    },
    onError: () => {
      console.error('Failed to fetch issues logged per status.');
      setShowExport(true);
    },
  });

  const chartData = useMemo(() => {
    const rawData = apiResponse?.data;
    if (!rawData || !Array.isArray(rawData)) return [];

    return rawData.map((item) => ({
      status: item.status,
      count: item.count,
      fill: STATUS_COLORS[item.status] || getRandomColor(),
    }));
  }, [apiResponse]);

  const customIssueType = apiResponse?.custom_issue_type || 'Enhancements';
  const handleRefresh = () => {
    forceRefreshRef.current = true;
    queryClient.refetchQueries({ queryKey, exact: true });
  };
  return (
    <div className="chart-div">
      <ChartTitle
        title={`${customIssueType} Logged By Status`}
        showWarning={!isJiraIntegrated}
        dateFilters={dateFilters}
        onRefresh={handleRefresh}
        isFetching={isFetching}
        showRefreshButton={!isLoading && isJiraIntegrated}
      />

      {isLoading ? (
        <Spinner className="mt-5" />
      ) : chartData.length === 0 ? (
        <NoDataAvailable />
      ) : (
        <ResponsiveContainer width="100%" height={300}>
          <BarChart
            barSize={25}
            data={chartData}
            margin={{ top: 20, right: 30, left: 20, bottom: 30 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="status"
              label={{ value: 'Status', position: 'insideBottom', offset: -10 }}
              tick={{ fontSize: 12 }}
            />
            <YAxis allowDecimals={false} />
            <Tooltip />
            <Bar
              dataKey="count"
              name={customIssueType}
              shape={({ x, y, width, height, index }) => (
                <rect
                  x={x}
                  y={y}
                  width={width}
                  height={height}
                  fill={chartData[index]?.fill}
                />
              )}
            />
          </BarChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default IssuesLoggedPerStatusForCustomType;
